import React, { createContext, useContext, useMemo, useState } from 'react';

import it from './locales/it.json';
import en from './locales/en.json';
import es from './locales/es.json';
import fr from './locales/fr.json';

const I18nContext = createContext({ t: (k, p) => k, locale: 'it', setLocale: () => {} });

const SUPPORTED = ['it', 'en', 'fr', 'es'];
const OG_LOCALE_MAP = {
  it: 'it_IT',
  en: 'en_US',
  es: 'es_ES',
  fr: 'fr_FR'
};

const DICTS = { it, en, es, fr };

function resolveLocale(browserLang) {
  if (!browserLang) return 'it';
  const base = browserLang.toLowerCase().split('-')[0];
  return SUPPORTED.includes(base) ? base : 'it';
}

function interpolate(str, params) {
  if (!params) return str;
  return Object.keys(params).reduce((acc, key) => acc.replace(new RegExp(`{${key}}`, 'g'), params[key]), str);
}

export function I18nProvider({ children, initialLocale }) {
  let init = initialLocale;
  if (!init && typeof window !== 'undefined') {
    const qs = new URLSearchParams(window.location.search);
    const qlang = qs.get('lang');
    const stored = window.localStorage ? localStorage.getItem('wb_lang') : null;
    init = qlang || stored || (typeof navigator !== 'undefined' ? navigator.language : 'it');
  }
  const detected = resolveLocale(init);
  const [locale, _setLocale] = useState(detected);

  const t = useMemo(() => {
    const dict = DICTS[locale] || DICTS.it;
    return (key, params) => {
      const parts = key.split('.');
      let cur = dict;
      for (const p of parts) {
        if (cur && Object.prototype.hasOwnProperty.call(cur, p)) cur = cur[p];
        else { cur = null; break; }
      }
      const val = (typeof cur === 'string') ? cur : key;
      return interpolate(val, params);
    };
  }, [locale]);

  const setLocale = (loc) => {
    const next = resolveLocale(loc);
    _setLocale(next);
    try { localStorage.setItem('wb_lang', next); } catch {}
  };

  const value = useMemo(() => ({ t, locale, setLocale, ogLocale: OG_LOCALE_MAP[locale] || 'it_IT' }), [t, locale]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  return useContext(I18nContext);
}


